import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { S3FileItem, S3FolderItem } from '../../../services/query/aiPrompt/S3BrowserQuery.services';
import { canOpenFile } from './SCUIS3Browser.helpers';

export type CtxTarget = { kind: 'folder'; item: S3FolderItem } | { kind: 'file'; item: S3FileItem };
export type CtxMenuState = { mouseX: number; mouseY: number; target: CtxTarget };

type S3BrowserContextMenuProps = {
    ctxMenu: CtxMenuState | null;
    onClose: () => void;
    readOnly?: boolean;
    disableRename?: boolean;
    disableMove?: boolean;
    disableDownload?: boolean;
    disableDelete?: boolean;
    showPreviewPanel: boolean;
    downloading: boolean;
    onOpenFolder: (key: string) => void;
    onOpenFile: (file: S3FileItem) => void;
    onRename: (target: CtxTarget) => void;
    onMove: (target: CtxTarget) => void;
    onDownloadFolder: (folderKey: string) => void;
    onDownloadFile: (file: S3FileItem) => void;
    onDelete: (target: CtxTarget) => void;
};

const buildFolderMenuItems = (
    target: CtxTarget & { kind: 'folder' },
    props: S3BrowserContextMenuProps,
    t: (key: string) => string
): React.ReactNode[] => {
    const items: React.ReactNode[] = [
        <MenuItem
            key="open"
            onClick={() => {
                props.onOpenFolder(target.item.key);
                props.onClose();
            }}
        >
            <ListItemIcon>
                <FolderOpenIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('aiPrompt.s3Browser.ctxOpen')} />
        </MenuItem>,
        <MenuItem
            key="rename"
            onClick={() => props.onRename(target)}
            disabled={props.readOnly || props.disableRename}
        >
            <ListItemIcon>
                <DriveFileRenameOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('aiPrompt.s3Browser.ctxRename')} />
        </MenuItem>,
        <MenuItem key="move" onClick={() => props.onMove(target)} disabled={props.readOnly || props.disableMove}>
            <ListItemIcon>
                <DriveFileMoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('aiPrompt.s3Browser.ctxMove')} />
        </MenuItem>,
    ];

    if (!props.disableDownload) {
        items.push(
            <MenuItem
                key="download-folder"
                onClick={() => props.onDownloadFolder(target.item.key)}
                disabled={props.downloading}
            >
                <ListItemIcon>
                    <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('aiPrompt.s3Browser.ctxDownloadFolder')} />
            </MenuItem>
        );
    }

    if (!props.disableDelete) {
        items.push(
            <MenuItem
                key="delete"
                onClick={() => {
                    props.onClose();
                    props.onDelete(target);
                }}
                disabled={props.readOnly}
            >
                <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText primary={t('aiPrompt.s3Browser.ctxDelete')} sx={{ color: 'error.main' }} />
            </MenuItem>
        );
    }

    return items;
};

const buildFileMenuItems = (
    target: CtxTarget & { kind: 'file' },
    props: S3BrowserContextMenuProps,
    t: (key: string) => string
): React.ReactNode[] => {
    const file = target.item;
    const items: React.ReactNode[] = [
        <MenuItem
            key="open"
            onClick={() => {
                props.onOpenFile(file);
                props.onClose();
            }}
            disabled={!props.showPreviewPanel || !canOpenFile(file)}
        >
            <ListItemIcon>
                <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('aiPrompt.s3Browser.ctxOpen')} />
        </MenuItem>,
        <MenuItem
            key="rename"
            onClick={() => props.onRename(target)}
            disabled={props.readOnly || props.disableRename}
        >
            <ListItemIcon>
                <DriveFileRenameOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('aiPrompt.s3Browser.ctxRename')} />
        </MenuItem>,
        <MenuItem key="move" onClick={() => props.onMove(target)} disabled={props.readOnly || props.disableMove}>
            <ListItemIcon>
                <DriveFileMoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('aiPrompt.s3Browser.ctxMove')} />
        </MenuItem>,
    ];

    if (!props.disableDownload) {
        items.push(
            <MenuItem key="dl" onClick={() => props.onDownloadFile(file)} disabled={props.downloading}>
                <ListItemIcon>
                    <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('aiPrompt.s3Browser.ctxDownload')} />
            </MenuItem>
        );
    }

    if (!props.disableDelete) {
        items.push(
            <MenuItem
                key="delete"
                onClick={() => {
                    props.onClose();
                    props.onDelete(target);
                }}
                disabled={props.readOnly}
            >
                <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText primary={t('aiPrompt.s3Browser.ctxDelete')} sx={{ color: 'error.main' }} />
            </MenuItem>
        );
    }

    return items;
};

export const S3BrowserContextMenu: React.FC<S3BrowserContextMenuProps> = (props) => {
    const { t } = useTranslation();
    const { ctxMenu } = props;

    const menuItems =
        ctxMenu?.target.kind === 'folder'
            ? buildFolderMenuItems(ctxMenu.target, props, t)
            : ctxMenu?.target.kind === 'file'
              ? buildFileMenuItems(ctxMenu.target, props, t)
              : null;

    return (
        <Menu
            open={ctxMenu !== null}
            onClose={props.onClose}
            anchorReference="anchorPosition"
            anchorPosition={ctxMenu !== null ? { top: ctxMenu.mouseY, left: ctxMenu.mouseX } : undefined}
            slotProps={{
                paper: {
                    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
                },
            }}
        >
            {menuItems}
        </Menu>
    );
};
